import React, { useCallback, useEffect, useMemo, useState } from "react";
import cogoToast from "cogo-toast";
import {
  Button,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
} from "reactstrap";
import { FaCog, FaEdit, FaSearch, FaSync } from "react-icons/fa";
import { genericGet, genericPost } from "../../services/Api";
import { ENDPOINTS, SUCCESS_RESULT } from "../../Constants";
import "react-overlay-loader/styles.css";
import { Loader } from "react-overlay-loader";

const formatCell = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const formatSettingsPreview = (settings) => {
  if (!settings || typeof settings !== "object") return "—";
  const entries = Object.entries(settings);
  if (entries.length === 0) return "—";
  return entries.map(([key, value]) => `${key}: ${formatCell(value)}`).join(" · ");
};

const settingsToFormValues = (settings) => {
  if (!settings || typeof settings !== "object") return {};
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => {
      if (typeof value === "object" && value !== null) {
        return [key, JSON.stringify(value, null, 2)];
      }
      return [key, value ?? ""];
    })
  );
};

const parseFormValue = (rawValue, originalValue) => {
  if (typeof originalValue === "boolean") {
    return rawValue === true || rawValue === "true";
  }
  if (typeof originalValue === "number") {
    const parsed = Number(rawValue);
    return Number.isNaN(parsed) ? rawValue : parsed;
  }
  if (typeof originalValue === "object" && originalValue !== null) {
    try {
      return JSON.parse(rawValue);
    } catch {
      throw new Error("Invalid JSON in one or more settings fields.");
    }
  }
  return rawValue;
};

const Settings = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [tableSearch, setTableSearch] = useState("");
  const [editTarget, setEditTarget] = useState(null);
  const [formValues, setFormValues] = useState({});
  const [showEditModal, setShowEditModal] = useState(false);

  const fetchSettings = useCallback(() => {
    setIsLoading(true);
    genericGet(ENDPOINTS.EBMS_SETTINGS_LIST)
      .then((res) => {
        setIsLoading(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          setItems(Array.isArray(res.data.data) ? res.data.data : []);
        } else {
          cogoToast.error(res?.data?.message || "Failed to load settings.");
        }
      })
      .catch(() => {
        setIsLoading(false);
        cogoToast.error("Failed to load settings.");
      });
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const filteredItems = useMemo(() => {
    if (!tableSearch.trim()) return items;
    const q = tableSearch.trim().toLowerCase();
    return items.filter((item) => {
      const haystack = [
        item?.identifier,
        item?.status,
        item?.created_at,
        item?.updated_at,
        formatSettingsPreview(item?.settings),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [items, tableSearch]);

  const openEditModal = (item) => {
    setEditTarget(item);
    setFormValues(settingsToFormValues(item?.settings));
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    if (isSaving) return;
    setShowEditModal(false);
    setEditTarget(null);
    setFormValues({});
  };

  const handleFieldChange = (key, value) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  };

  const saveSettings = () => {
    if (!editTarget?.id) return;

    const originalSettings = editTarget?.settings || {};
    let settings;
    try {
      settings = Object.fromEntries(
        Object.keys(originalSettings).map((key) => [
          key,
          parseFormValue(formValues[key], originalSettings[key]),
        ])
      );
    } catch (err) {
      cogoToast.error(err.message || "Invalid settings values.");
      return;
    }

    setIsSaving(true);
    genericPost(ENDPOINTS.EBMS_SETTINGS_UPDATE, {
      id: editTarget.id,
      settings,
    })
      .then((res) => {
        setIsSaving(false);
        if (res.status === 200 && res.data.status === SUCCESS_RESULT) {
          cogoToast.success(res.data.message || "Settings updated.");
          closeEditModal();
          fetchSettings();
        } else {
          cogoToast.error(res?.data?.message || "Failed to update settings.");
        }
      })
      .catch(() => {
        setIsSaving(false);
        cogoToast.error("Failed to update settings.");
      });
  };

  const editFields = useMemo(() => {
    if (!editTarget?.settings || typeof editTarget.settings !== "object") return [];
    return Object.entries(editTarget.settings).map(([key, value]) => ({
      key,
      label: key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
      type:
        typeof value === "boolean"
          ? "boolean"
          : typeof value === "object" && value !== null
            ? "json"
            : "text",
    }));
  }, [editTarget]);

  return (
    <div className="default-card-style">
      <Loader loading={isLoading && !showEditModal} />

      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <h2 className="mb-1" style={{ fontWeight: 700 }}>
            <FaCog className="me-2" />
            EBMS Settings
          </h2>
          <p className="text-muted mb-0">View and update application configuration settings.</p>
        </div>
        <Button color="primary" onClick={fetchSettings} disabled={isLoading}>
          <FaSync className="me-1" /> Refresh
        </Button>
      </div>

      <div className="mb-3">
        <div className="input-group" style={{ maxWidth: "480px" }}>
          <span className="input-group-text bg-primary text-white">
            <FaSearch />
          </span>
          <Input
            type="text"
            placeholder="Search identifier or settings..."
            value={tableSearch}
            onChange={(e) => setTableSearch(e.target.value)}
          />
        </div>
        <small className="text-muted d-block mt-2">
          {filteredItems.length} setting{filteredItems.length === 1 ? "" : "s"}
          {tableSearch ? ` matching "${tableSearch}"` : ""}
        </small>
      </div>

      <div className="table-responsive shadow-sm rounded border">
        <table className="table table-hover mb-0 align-middle" style={{ fontSize: "0.9rem" }}>
          <thead className="table-dark">
            <tr>
              <th style={{ width: "44px" }}>#</th>
              <th style={{ minWidth: "160px" }}>Identifier</th>
              <th style={{ minWidth: "240px" }}>Settings</th>
              <th style={{ minWidth: "130px" }}>Updated</th>
              <th style={{ width: "90px" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && items.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  <Spinner size="sm" className="me-2" /> Loading settings...
                </td>
              </tr>
            ) : filteredItems.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  {tableSearch ? "No settings match your search." : "No settings found."}
                </td>
              </tr>
            ) : (
              filteredItems.map((item, index) => (
                <tr key={item.id || index}>
                  <td className="text-muted fw-semibold">{index + 1}</td>
                  <td>
                    <div className="fw-semibold">{item?.identifier || "—"}</div>
                    <small className="text-muted d-block text-truncate" style={{ maxWidth: "220px" }} title={item?.id}>
                      {item?.id || "—"}
                    </small>
                  </td>
                  <td>
                    <div
                      className="text-truncate"
                      style={{ maxWidth: "360px" }}
                      title={formatSettingsPreview(item?.settings)}
                    >
                      {formatSettingsPreview(item?.settings)}
                    </div>
                  </td>
                  <td>
                    <small className="text-muted d-block">{item?.updated_at || "—"}</small>
                  </td>
                  <td>
                    <Button color="success" size="sm" onClick={() => openEditModal(item)}>
                      <FaEdit /> Edit
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal isOpen={showEditModal} toggle={closeEditModal} centered size="lg">
        <ModalHeader toggle={closeEditModal}>
          Edit Settings — {editTarget?.identifier || "—"}
        </ModalHeader>
        <ModalBody>
          {editFields.length === 0 ? (
            <p className="text-muted mb-0">No editable settings fields found.</p>
          ) : (
            editFields.map(({ key, label, type }) => (
              <div className="mb-3" key={key}>
                <Label for={`setting-${key}`} className="fw-semibold">
                  {label}
                </Label>
                {type === "boolean" ? (
                  <Input
                    id={`setting-${key}`}
                    type="select"
                    value={String(formValues[key] ?? false)}
                    onChange={(e) => handleFieldChange(key, e.target.value === "true")}
                  >
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </Input>
                ) : type === "json" ? (
                  <Input
                    id={`setting-${key}`}
                    type="textarea"
                    rows={4}
                    value={formValues[key] ?? ""}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                    style={{ fontFamily: "monospace", fontSize: "0.85rem" }}
                  />
                ) : (
                  <Input
                    id={`setting-${key}`}
                    type="text"
                    value={formValues[key] ?? ""}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                  />
                )}
              </div>
            ))
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={closeEditModal} disabled={isSaving}>
            Cancel
          </Button>
          <Button color="primary" onClick={saveSettings} disabled={isSaving || !editTarget?.id}>
            {isSaving ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Save changes"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default Settings;
